import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, finalize } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Track 401 error count for each request to detect retries
 * Maps request URL to error count and timestamp
 */
const error401Map = new Map<string, { count: number; timestamp: number }>();

/**
 * Time window (in ms) to consider a 401 as part of the same request chain
 * Entries older than this will be cleaned up
 */
const RETRY_WINDOW_MS = 10000;

/**
 * HTTP Interceptor to handle authentication errors
 * Automatically logs out users and redirects to login when receiving 401 Unauthorized responses
 * Will only logout if a retry also returns 401, allowing for transient token issues
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const requestKey = `${req.method}:${req.url}`;
  
  // Add Authorization header if token exists and request doesn't already have one
  // (e.g. clean_data uses a project-specific token set directly on the request)
  const token = authService.getToken();
  if (token && !req.headers.has('Authorization')) {
    req = req.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check if this is a 401 Unauthorized error
      if (error.status === 401) {
        const now = Date.now();
        const errorInfo = error401Map.get(requestKey);
        
        if (errorInfo) {
          // We've seen this request fail with 401 before
          const timeSinceLastError = now - errorInfo.timestamp;
          console.log(`[Auth Interceptor] 401 #${errorInfo.count + 1} for ${requestKey}, time since last: ${timeSinceLastError}ms`);
          
          if (timeSinceLastError < RETRY_WINDOW_MS) {
            // This is a retry that also failed
            errorInfo.count++;
            errorInfo.timestamp = now;
            
            if (errorInfo.count >= 2) {
              // Second 401 (or more) - logout
              console.warn('[Auth Interceptor] Multiple 401 errors detected. Logging out user.');
              error401Map.delete(requestKey);
              
              authService.logout();
              router.navigate(['/login'], {
                queryParams: { returnUrl: router.url }
              });
            }
          } else {
            // Too much time passed, treat as a new request
            console.log(`[Auth Interceptor] Resetting counter for ${requestKey} (timeout)`);
            error401Map.set(requestKey, { count: 1, timestamp: now });
          }
        } else {
          // First 401 for this request
          console.log(`[Auth Interceptor] First 401 for ${requestKey}, tracking for retry`);
          error401Map.set(requestKey, { count: 1, timestamp: now });
        }
        
        // Clean up old entries
        for (const [key, info] of error401Map.entries()) {
          if (now - info.timestamp >= RETRY_WINDOW_MS) {
            error401Map.delete(key);
          }
        }
      } else {
        // Non-401 error, clear any 401 tracking for this request
        error401Map.delete(requestKey);
      }
      
      // Re-throw the error so it can be handled by the service's error handler
      return throwError(() => error);
    }),
    finalize(() => {
      // Log when the observable completes or errors out
      
      // Clear tracking on successful completion (no error)
      // This will run after successful requests
      const errorInfo = error401Map.get(requestKey);
      if (errorInfo) {
        console.log(`[Auth Interceptor] Request completed, had ${errorInfo.count} 401s tracked`);
      }
    })
  );
};
